// Pecas pequenas de interface, sem regra de jogo dentro.

import React, { useEffect, useState } from 'react';
import { money } from '../engine/economy.js';
import { POSITION_LABEL } from '../engine/players.js';
import { formatMinutes, derive } from '../engine/boxscore.js';

export const fmtMoney = money;

export function Ovr({ value }) {
  const cls = value >= 88 ? 'elite' : value >= 82 ? 'great' : value >= 76 ? 'good' : '';
  return <span className={`ovr ${cls}`}>{value}</span>;
}

export function Stat({ k, v, tone }) {
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className={`v ${tone || ''}`}>{v}</div>
    </div>
  );
}

export function Pill({ children, on }) {
  return <span className={`pill ${on ? 'on' : ''}`}>{children}</span>;
}

export function Modal({ title, onClose, children, footer }) {
  // Fechar com Esc: teclado tambem e usuario.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="row spread" style={{ marginBottom: 12 }}>
          <h1>{title}</h1>
          <button className="ghost sm" onClick={onClose}>fechar</button>
        </div>
        {children}
        {footer && <div className="row wrap" style={{ marginTop: 14, gap: 8 }}>{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Avisos. TODO evento do motor passa por aqui -- no projeto anterior, mensagem
 * que so aparecia numa tela especifica era mensagem que ninguem lia.
 */
export function Toasts({ items }) {
  return (
    <div className="toasts">
      {items.map((t) => <div key={t.id} className={`toast ${t.kind || 'info'}`}>{t.text}</div>)}
    </div>
  );
}

export function useToasts() {
  const [items, setItems] = useState([]);
  const push = (events) => {
    const list = (Array.isArray(events) ? events : [events]).filter(Boolean);
    if (!list.length) return;
    const stamped = list.map((e, i) => ({ ...e, id: `${Date.now()}-${i}-${Math.random()}` }));
    setItems((cur) => [...cur, ...stamped].slice(-4));
    for (const t of stamped) {
      setTimeout(() => setItems((cur) => cur.filter((x) => x.id !== t.id)), t.kind === 'title' ? 9000 : 5200);
    }
  };
  return [items, push];
}

/** Linha de atleta na tabela de elenco. */
export function PlayerRow({ player, onClick, extra, highlight }) {
  return (
    <tr className={`click ${highlight ? 'me' : ''}`} onClick={onClick}>
      <td className="n">
        {player.name}
        <span className="sub">{POSITION_LABEL[player.position]} · {player.age} anos</span>
      </td>
      <td><Ovr value={player.overall} /></td>
      <td className="muted">{player.potential}</td>
      <td>{fmtMoney(player.marketValue)}</td>
      <td className="muted">{fmtMoney(player.wage)}</td>
      {extra}
    </tr>
  );
}

/** Sumula completa de uma partida. */
export function BoxScore({ box }) {
  const periods = box.home.quarters.map((_, i) => (i < 4 ? `${i + 1}Q` : `PR${i - 3}`));
  const side = (s) => {
    const used = s.lines.filter((l) => l.seconds > 0).sort((a, b) => b.seconds - a.seconds);
    return (
      <div key={s.teamId}>
        <h2>{s.name} <span className="acc" style={{ fontSize: 20 }}>{s.score}</span></h2>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th className="n">atleta</th><th>min</th><th>pts</th><th>quadra</th>
                <th>3pt</th><th>ll</th><th>reb</th><th>ast</th><th>er</th><th>+/-</th>
              </tr>
            </thead>
            <tbody>
              {used.map((l) => (
                <tr key={l.playerId}>
                  <td className="n">{l.name}<span className="sub">{l.position}</span></td>
                  <td className="muted">{formatMinutes(l.seconds)}</td>
                  <td style={{ fontWeight: 700 }}>{l.pts}</td>
                  <td>{l.fgm}/{l.fga}</td>
                  <td>{l.tpm}/{l.tpa}</td>
                  <td>{l.ftm}/{l.fta}</td>
                  <td>{l.orb + l.drb}</td>
                  <td>{l.ast}</td>
                  <td className="muted">{l.tov}</td>
                  <td className={l.plusMinus > 0 ? 'pos' : l.plusMinus < 0 ? 'neg' : 'muted'}>
                    {l.plusMinus > 0 ? '+' : ''}{l.plusMinus}
                  </td>
                </tr>
              ))}
              <tr className="tot">
                <td className="n">TOTAL</td><td /><td style={{ fontWeight: 700 }}>{s.score}</td>
                <td>{s.totals.fgm}/{s.totals.fga}</td>
                <td>{s.totals.tpm}/{s.totals.tpa}</td>
                <td>{s.totals.ftm}/{s.totals.fta}</td>
                <td>{s.totals.orb + s.totals.drb}</td>
                <td>{s.totals.ast}</td><td>{s.totals.tov}</td><td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="scroll">
        <table>
          <thead>
            <tr><th className="n" />{periods.map((p) => <th key={p}>{p}</th>)}<th>final</th></tr>
          </thead>
          <tbody>
            {[box.away, box.home].map((s) => (
              <tr key={s.teamId}>
                <td className="n">{s.name}</td>
                {s.quarters.map((q, i) => <td key={i} className="muted">{q}</td>)}
                <td style={{ fontWeight: 700 }}>{s.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {side(box.away)}
      {side(box.home)}
      {box.log && box.log.length > 0 && (
        <>
          <h2>Fim de jogo</h2>
          <div className="card tight small">
            {box.log.map((ev, i) => {
              const mm = Math.floor(Math.max(ev.clock, 0) / 60);
              const ss = Math.floor(Math.max(ev.clock, 0) % 60);
              return (
                <div key={i} className="row" style={{ padding: '5px 0', gap: 12 }}>
                  <span className="muted tabular" style={{ minWidth: 58 }}>
                    {ev.q}Q {mm}:{String(ss).padStart(2, '0')}
                  </span>
                  <span>{ev.text}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export { derive };
